import type { Ctx } from './ctx';
import type { ViewModel } from '@/screens/generated/view-model';
import { api } from '@/client/api';

/** Dashboard widget visibility (mandate §5) — the Widgets screen's Edit Dashboard, server-backed: the
 *  toggle writes `me.dashboard.update` and the grid re-renders from the response. An absent server row means
 *  visible, so a fresh account shows the design's full resting render. */
export function buildWidgets(c: Ctx) {
  const W = c.S.dashW;
  const patch = (part: Partial<NonNullable<typeof W>>) =>
    c.set((st) => (st.dashW ? { dashW: { ...st.dashW, ...part } } : {}));

  const load = async () => {
    if (c.S.dashW?.loaded) return;
    if (!c.S.dashW) c.set({ dashW: { loaded: false, items: [], dialog: false } });
    try {
      const out = await api.me.dashboard.get();
      patch({ loaded: true, items: out.items.map((i) => ({ ...i })) });
    } catch {
      patch({ loaded: true }); // the full grid stands; the dialog will surface write errors
    }
  };

  return {
    dashWHydrate: () => void load(),
    /** Visibility gate for a card. True until the server says otherwise — the pre-load skeleton is the
     *  design's full grid. */
    dashShow: (key: string) => {
      if (!W?.loaded) return true;
      return W.items.find((i) => i.key === key)?.visible ?? true;
    },
    dashEdit: () => {
      if (!W?.loaded) {
        c.flash('Widget settings are still loading from the server — a moment.', 'mut');
        return;
      }
      patch({ dialog: true });
    },
    dashEditClose: () => patch({ dialog: false }),
    dashEditOpen: W?.dialog ?? false,
    dashWidgets: (W?.items ?? []).map((item) => {
      const t = c.tog(item.visible);
      return {
        ...item,
        trackBg: t.trackBg,
        knob: t.knob,
        knobBg: t.knobBg,
        toggle: () =>
          void api.me.dashboard
            .update({
              idempotencyKey: `dsh-${item.key}-${Date.now()}`,
              key: item.key,
              visible: !item.visible,
            })
            .then((out) => {
              patch({ items: out.items.map((i) => ({ ...i })) });
              c.flash(`${item.label} ${item.visible ? 'hidden' : 'shown'}.`, 'pos');
            })
            .catch((error: unknown) =>
              c.flash(error instanceof Error ? error.message : `${item.label} could not be changed.`, 'neg'),
            ),
      };
    }),
  } satisfies Partial<ViewModel>;
}
